import { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from '@raas/shared/src/utils/axios';
import {
  ILegalProvisionDeltaDetails,
  IWriteLegalProvision,
} from '@raas/shared/src/types/legal-provision';

class LegalProvisionsService {
  delete(id: string): Promise<void> {
    return axios.delete(`/legal-provisions/${id}`);
  }

  async create(postData: IWriteLegalProvision): Promise<string> {
    const result = await axios.post(`/legal-provisions`, mapToRequestBody(postData));
    return result.data;
  }

  async createNewVersion(id: string, versionName: string): Promise<string> {
    const result = await axios.post(`/legal-provisions/${id}/new-version`, {
      version: versionName,
    });
    return result.data;
  }

  async createDraft(id: string, versionId: string): Promise<string> {
    const result = await axios.post(`/legal-provisions/${id}/draft`);
    return result.data;
  }

  async edit(postData: IWriteLegalProvision, id: string): Promise<void> {
    return axios.put(`/legal-provisions/${id}`, mapToRequestBody(postData));
  }

  async getDelta(
    baseId: string,
    compareId: string,
    baseTranslationId?: string,
    compareTranslationId?: string,
    language?: string
  ): Promise<ILegalProvisionDeltaDetails | null> {
    const url = `/legal-provisions/${baseId}/delta?base-translation-id=${baseTranslationId}&compare-translation-id=${compareTranslationId}&compare-id=${compareId}`;
    const result = await axios.get(language ? `${url}&language=${language}` : url);
    return result.data;
  }

  async getDeltaExport(
    baseId: string,
    compareId: string,
    baseTranslationId?: string,
    compareTranslationId?: string,
    fileType?: string,
    exportOnlyUpdatedParagraphs?: boolean,
    language?: string
  ): Promise<{ filename: string; blob: Blob }> {
    const config: AxiosRequestConfig = {
      responseType: 'blob',
    };
    const url = `/legal-provisions/${baseId}/delta/export?base-translation-id=${baseTranslationId}&compare-translation-id=${compareTranslationId}&compare-id=${compareId}&file-type=${fileType}&only-updated=${exportOnlyUpdatedParagraphs}`;
    const result = await axios.get(language ? `${url}&language=${language}` : url, config);
    const blob = new Blob([result.data], { type: result.headers['content-type'] });
    const filename = this.getFilename(result, baseId);
    return { filename, blob };
  }

  async getLegalProvisionPlacementCandidates(
    baseId: string,
    compareId: string,
    documentElementId: string
  ): Promise<ILegalProvisionDeltaDetails | null> {
    const result = await axios.get(
      `/legal-provisions/${baseId}/de-moving?document-element-id=${documentElementId}&compare-id=${compareId}`
    );
    return result.data;
  }

  private getFilename(response: AxiosResponse, id: string): string {
    const contentDispositionHeader = response.headers['content-disposition'];
    const match =
      contentDispositionHeader && contentDispositionHeader.match(/filename=["']?([^"';\n]+)["']?/);
    return match ? match[1] : `${id}_export.pdf`;
  }
}

const mapToRequestBody = (legalProvision: IWriteLegalProvision) => ({
  id: legalProvision.id,
  fullName: legalProvision.fullName,
  abbreviation: legalProvision.abbreviation,
  originalVersion: legalProvision.originalVersion,
  validFrom: legalProvision.validFrom,
  validTo: legalProvision.validTo,
  dateOfPublication: legalProvision.dateOfPublication,
  dateOfAdoption: legalProvision.dateOfAdoption,
  publisher: legalProvision.publisher,
  type: legalProvision.type,
  sourceName: legalProvision.sourceName,
  sourceLink: legalProvision.sourceLink,
  tagIds: legalProvision.tagIds,
  versionName: legalProvision.versionName,
  releaseId: legalProvision.releaseId,
  isComplete: true,
  countryFlag: legalProvision.countryFlag,
  notes: legalProvision.notes,
  legalProvisionContent: legalProvision.legalProvisionContent,
});

const legalProvisionsService = new LegalProvisionsService();
export default legalProvisionsService;
